import React, { useState } from "react";
import { Loader2, Plus, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Btn, Input, Select } from "../UI";
import { errMsg, downloadBlob, FORMATS, THEMES } from "./format";
import { ScanCardsForm } from "./ScanCardsForm";

export const AllocatePanel = ({ ev, reload }) => {
  const [count, setCount] = useState(25);
  const [staffId, setStaffId] = useState("");
  const [busy, setBusy] = useState(false);
  const allocate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/promotions/events/${ev.id}/codes`, { count: parseInt(count) || 1, staff_id: staffId || null });
      toast.success(`${data.created} code(s) allocated${data.staff ? ` to ${data.staff.name}` : ""}`);
      reload();
    } catch (e) { toast.error(errMsg(e, "Could not allocate codes")); } finally { setBusy(false); }
  };
  return (
    <section data-testid="allocate-panel">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">Allocate codes to this trade show</div>
      <div className="flex items-end gap-2 flex-wrap">
        <Input label="How many" type="number" min="1" max="500" value={count} onChange={(e) => setCount(e.target.value)} className="w-28" data-testid="allocate-count-input" />
        <Select label="Hand directly to" value={staffId} onChange={(e) => setStaffId(e.target.value)} className="w-48" data-testid="allocate-staff-select">
          <option value="">Unassigned pool</option>
          {(ev.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <Btn onClick={allocate} disabled={busy} data-testid="allocate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Allocate
        </Btn>
      </div>
    </section>
  );
};

export const PrintPanel = ({ ev, reload }) => {
  const [cardType, setCardType] = useState("credit");
  const [format, setFormat] = useState(ev.card_format || "business");
  const [theme, setTheme] = useState(ev.card_theme || "dark");
  const [onlyUnclaimed, setOnlyUnclaimed] = useState(true);
  const [skipPrinted, setSkipPrinted] = useState(false);
  const [staffId, setStaffId] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/promotions/events/${ev.id}/print`,
        { format, theme, only_unclaimed: onlyUnclaimed, skip_printed: skipPrinted, staff_id: staffId || null, limit: 500 },
        { responseType: "blob" });
      const count = res.headers?.["x-card-count"];
      downloadBlob(res.data, `promo-cards-${format}-${ev.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`);
      toast.success(`PDF ready${count ? ` — ${count} card(s)` : ""}`);
      reload();
    } catch (e) {
      const detail = e?.response?.data instanceof Blob ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } }) : null;
      toast.error(detail || errMsg(e, "Could not generate the PDF"));
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="print-panel">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Print-ready cards (PDF)</div>
        <div className="flex gap-1 rounded-lg border border-white/10 p-0.5" data-testid="print-card-type-toggle">
          {[["credit", "Credit cards (codes)"], ["race", "Find Your Race (QR)"]].map(([k, l]) => (
            <button key={k} onClick={() => setCardType(k)} data-testid={`print-card-type-${k}`}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${cardType === k ? "bg-amber-400/15 text-amber-200" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {cardType === "race" ? <ScanCardsForm ev={ev} reload={reload} /> : (<>
      <div className="grid md:grid-cols-3 gap-3">
        <Select label="Format" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="print-format-select">
          {FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
        <div>
          <div className="text-xs text-slate-400 mb-1">Look</div>
          <div className="flex gap-1.5" data-testid="print-theme-toggle">
            {THEMES.map(([k, l]) => (
              <button key={k} onClick={() => setTheme(k)} data-testid={`print-theme-${k}`}
                className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors ${theme === k ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <Select label="Only cards handed to" value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="print-staff-select">
          <option value="">Everyone</option>
          {(ev.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
      </div>
      <div className="flex items-center gap-4 mt-3 flex-wrap text-xs text-slate-300">
        <label className="flex items-center gap-2"><input type="checkbox" checked={onlyUnclaimed} onChange={(e) => setOnlyUnclaimed(e.target.checked)} data-testid="print-only-unclaimed" /> Unclaimed codes only</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={skipPrinted} onChange={(e) => setSkipPrinted(e.target.checked)} data-testid="print-skip-printed" /> Skip already-printed cards</label>
        <Btn onClick={generate} disabled={busy} className="ml-auto" data-testid="print-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Generate PDF
        </Btn>
      </div>
      </>)}
      {(ev.print_runs || []).length > 0 && (
        <div className="mt-3 space-y-1" data-testid="print-runs">
          {ev.print_runs.slice(0, 5).map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-[11px] text-slate-400" data-testid={`print-run-${r.id}`}>
              <FileDown className="w-3 h-3" /> {r.created_at?.slice(0, 16).replace("T", " ")} · {r.count} {r.kind === "find_your_race" ? "Find-Your-Race card(s)" : "card(s)"} · {r.format} · {r.theme} · {r.created_by}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
